import { create } from 'zustand';
import { getUserPref, setUserPref } from '../hooks/useUserPreferences';

// ============================================================================
// Selective Touch Display Settings Store
// ============================================================================

export type ButtonStyle = 'vibrant' | 'pastel' | 'neutral';
export type CardDensity = 'compact' | 'normal' | 'spacious';
export type StatusStyle = 'full' | 'muted';
export type UnitColorMode = 'facility' | 'neutral';

/** The button style choices, as Selective Touch's settings panel offers them (also on Settings). */
export const ST_BUTTON_STYLE_OPTIONS: ReadonlyArray<{ value: ButtonStyle; label: string }> = [
  { value: 'vibrant', label: 'Vibrant' },
  { value: 'pastel', label: 'Pastel' },
  { value: 'neutral', label: 'Neutral' },
];

/** The card density choices (Settings). */
export const ST_CARD_DENSITY_OPTIONS: ReadonlyArray<{ value: CardDensity; label: string }> = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normal' },
  { value: 'spacious', label: 'Spacious' },
];

/** A foreground + background color pair */
export interface ColorPair {
  bg: string;
  fg: string;
}

/** User-overridable color slots */
export interface ColorOverrides {
  // Status card pairs (bg + fg)
  orderSaved?: ColorPair;
  orderPrinted?: ColorPair;
  printedNotSaved?: ColorPair;
  npo?: ColorPair;
  noOrder?: ColorPair;
  // Touch button default
  touchButtonDefault?: ColorPair;
  // Chrome
  headerBg?: string;
  footerBg?: string;
  accent?: string;
  // Per-unit overrides (keyed by unit name)
  units?: Record<string, ColorPair>;
  // Per-page-tab overrides (keyed by PKey string)
  pageTabs?: Record<string, ColorPair>;
}

/** Named preset theme */
export interface PresetTheme {
  id: string;
  name: string;
  desc: string;
  overrides: ColorOverrides;
  buttonStyle: ButtonStyle;
  statusStyle: StatusStyle;
}

export const PRESET_THEMES: PresetTheme[] = [
  {
    id: 'default',
    name: 'Default',
    desc: 'Factory facility colors',
    overrides: {},
    buttonStyle: 'vibrant',
    statusStyle: 'full',
  },
  {
    id: 'modern-flat',
    name: 'Modern Flat',
    desc: 'Neutral chrome, muted status',
    overrides: {
      orderSaved: { bg: '#059669', fg: '#ffffff' },
      orderPrinted: { bg: '#2563eb', fg: '#ffffff' },
      printedNotSaved: { bg: '#6b7280', fg: '#ffffff' },
      npo: { bg: '#dc2626', fg: '#ffffff' },
      noOrder: { bg: '#e2e8f0', fg: '#334155' },
      touchButtonDefault: { bg: '#475569', fg: '#ffffff' },
      accent: '#2563eb',
    },
    buttonStyle: 'neutral',
    statusStyle: 'muted',
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    desc: 'Calm blues and greens',
    overrides: {
      orderSaved: { bg: '#16a34a', fg: '#ffffff' },
      orderPrinted: { bg: '#0ea5e9', fg: '#ffffff' },
      printedNotSaved: { bg: 'var(--text-muted)', fg: '#ffffff' },
      npo: { bg: '#ef4444', fg: '#ffffff' },
      noOrder: { bg: '#dbeafe', fg: '#1e40af' },
      touchButtonDefault: { bg: '#0284c7', fg: '#ffffff' },
      accent: '#0284c7',
    },
    buttonStyle: 'pastel',
    statusStyle: 'full',
  },
  {
    id: 'high-contrast',
    name: 'High Contrast',
    desc: 'Bold colors, maximum visibility',
    overrides: {
      orderSaved: { bg: '#15803d', fg: '#ffffff' },
      orderPrinted: { bg: '#1d4ed8', fg: '#ffffff' },
      printedNotSaved: { bg: '#525252', fg: '#ffffff' },
      npo: { bg: '#b91c1c', fg: '#ffffff' },
      noOrder: { bg: '#fbbf24', fg: '#000000' },
      touchButtonDefault: { bg: '#1e3a5f', fg: '#ffffff' },
      accent: '#1d4ed8',
    },
    buttonStyle: 'vibrant',
    statusStyle: 'full',
  },
  {
    id: 'warm',
    name: 'Warm',
    desc: 'Soft warm tones',
    overrides: {
      orderSaved: { bg: '#65a30d', fg: '#ffffff' },
      orderPrinted: { bg: '#7c3aed', fg: '#ffffff' },
      printedNotSaved: { bg: '#a8a29e', fg: '#ffffff' },
      npo: { bg: '#dc2626', fg: '#ffffff' },
      noOrder: { bg: '#fef3c7', fg: '#92400e' },
      touchButtonDefault: { bg: '#92400e', fg: '#ffffff' },
      accent: '#d97706',
    },
    buttonStyle: 'pastel',
    statusStyle: 'full',
  },
];

interface STDisplayState {
  buttonStyle: ButtonStyle;
  cardDensity: CardDensity;
  statusStyle: StatusStyle;
  unitColors: UnitColorMode;
  colorOverrides: ColorOverrides;
  activeThemeId: string | null;

  setButtonStyle: (v: ButtonStyle) => void;
  setCardDensity: (v: CardDensity) => void;
  setStatusStyle: (v: StatusStyle) => void;
  setUnitColors: (v: UnitColorMode) => void;
  setColorOverride: <K extends keyof ColorOverrides>(key: K, value: ColorOverrides[K]) => void;
  setUnitColorOverride: (unitName: string, color: ColorPair | undefined) => void;
  setPageTabColorOverride: (pKey: string, color: ColorPair | undefined) => void;
  applyTheme: (theme: PresetTheme) => void;
  resetAllColors: () => void;
  resetSection: (keys: (keyof ColorOverrides)[]) => void;

  syncWithUser: (userId: number) => void;
}

const PREFIX = 'st.display';
let currentUserId = 0;

function persist(key: string, value: unknown) {
  try { localStorage.setItem(`st_display_${key}`, JSON.stringify(value)); } catch { /* */ }
  if (currentUserId > 0) setUserPref(currentUserId, `${PREFIX}.${key}`, value);
}

function loadGlobal<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`st_display_${key}`);
    if (raw !== null) return JSON.parse(raw) as T;
  } catch { /* */ }
  return fallback;
}

const initialButtonStyle = loadGlobal<ButtonStyle>('buttonStyle', 'pastel');
const initialCardDensity = loadGlobal<CardDensity>('cardDensity', 'normal');
const initialStatusStyle = loadGlobal<StatusStyle>('statusStyle', 'full');
const initialUnitColors = loadGlobal<UnitColorMode>('unitColors', 'neutral');
const initialColorOverrides = loadGlobal<ColorOverrides>('colorOverrides', {});
const initialThemeId = loadGlobal<string | null>('activeThemeId', null);

export const useSTDisplayStore = create<STDisplayState>((set) => ({
  buttonStyle: initialButtonStyle,
  cardDensity: initialCardDensity,
  statusStyle: initialStatusStyle,
  unitColors: initialUnitColors,
  colorOverrides: initialColorOverrides,
  activeThemeId: initialThemeId,

  setButtonStyle: (v) => { persist('buttonStyle', v); set({ buttonStyle: v, activeThemeId: null }); persist('activeThemeId', null); },
  setCardDensity: (v) => { persist('cardDensity', v); set({ cardDensity: v }); },
  setStatusStyle: (v) => { persist('statusStyle', v); set({ statusStyle: v, activeThemeId: null }); persist('activeThemeId', null); },
  setUnitColors: (v) => { persist('unitColors', v); set({ unitColors: v }); },

  setColorOverride: (key, value) => {
    set((state) => {
      const next = { ...state.colorOverrides };
      if (value === undefined) { delete next[key]; } else { (next as Record<string, unknown>)[key as string] = value; }
      persist('colorOverrides', next);
      return { colorOverrides: next, activeThemeId: null };
    });
    persist('activeThemeId', null);
  },

  setUnitColorOverride: (unitName, color) => {
    set((state) => {
      const units = { ...state.colorOverrides.units };
      if (color === undefined) { delete units[unitName]; } else { units[unitName] = color; }
      const next = { ...state.colorOverrides, units: Object.keys(units).length > 0 ? units : undefined };
      if (!next.units) delete next.units;
      persist('colorOverrides', next);
      return { colorOverrides: next };
    });
  },

  setPageTabColorOverride: (pKey, color) => {
    set((state) => {
      const pageTabs = { ...state.colorOverrides.pageTabs };
      if (color === undefined) { delete pageTabs[pKey]; } else { pageTabs[pKey] = color; }
      const next = { ...state.colorOverrides, pageTabs: Object.keys(pageTabs).length > 0 ? pageTabs : undefined };
      if (!next.pageTabs) delete next.pageTabs;
      persist('colorOverrides', next);
      return { colorOverrides: next };
    });
  },

  applyTheme: (theme) => {
    persist('colorOverrides', theme.overrides);
    persist('buttonStyle', theme.buttonStyle);
    persist('statusStyle', theme.statusStyle);
    persist('activeThemeId', theme.id);
    set({
      colorOverrides: theme.overrides,
      buttonStyle: theme.buttonStyle,
      statusStyle: theme.statusStyle,
      activeThemeId: theme.id,
    });
  },

  resetAllColors: () => {
    persist('colorOverrides', {});
    persist('activeThemeId', 'default');
    set({ colorOverrides: {}, activeThemeId: 'default' });
  },

  resetSection: (keys) => {
    set((state) => {
      const next = { ...state.colorOverrides };
      keys.forEach((k) => delete next[k]);
      persist('colorOverrides', next);
      return { colorOverrides: next, activeThemeId: null };
    });
    persist('activeThemeId', null);
  },

  syncWithUser: (userId: number) => {
    currentUserId = userId;
    if (userId <= 0) return;
    const updates: Partial<STDisplayState> = {};
    const bs = getUserPref<ButtonStyle | null>(userId, `${PREFIX}.buttonStyle`, null);
    if (bs) updates.buttonStyle = bs;
    const cd = getUserPref<CardDensity | null>(userId, `${PREFIX}.cardDensity`, null);
    if (cd) updates.cardDensity = cd;
    const ss = getUserPref<StatusStyle | null>(userId, `${PREFIX}.statusStyle`, null);
    if (ss) updates.statusStyle = ss;
    const uc = getUserPref<UnitColorMode | null>(userId, `${PREFIX}.unitColors`, null);
    if (uc) updates.unitColors = uc;
    const co = getUserPref<ColorOverrides | null>(userId, `${PREFIX}.colorOverrides`, null);
    if (co) updates.colorOverrides = co;
    const tid = getUserPref<string | null>(userId, `${PREFIX}.activeThemeId`, null);
    if (tid) updates.activeThemeId = tid;
    if (Object.keys(updates).length > 0) set(updates);
  },
}));
